import { ReportsPriorityFeedQuery } from 'capability-ng-reports';
import { StudyGroupsPriorityFeedQuery } from 'capability-ng-study-groups';
import { SurveyPriorityFeedQuery } from 'capability-ng-survey';
import { ConversationsPriorityFeedQuery } from 'capability-ng-conversations';
import { Conversation } from 'capability-ng-conversations/src/builders/conversation_builder';
import { IConversationDatasourceModel } from 'capability-ng-conversations/src/conversations_interface';
import { PathItem } from 'capability-ng-todos';
import { ToDoPriorityFeedQuery, AppointmentRequestPriorityFeedQuery } from 'capability-ng-todos';
import { ReminderListDatastore } from 'data-ng-personal-reminders';
import { StaffTodoListDatastore } from 'data-ng-staff-todos';
import { IQuery } from 'core-ts-types';
import { IDatasource } from 'utility-ts-orm';
import * as _4 from 'lodash4';
import { Query } from 'utility-ts-orm';
import { StudentMenu } from '../builders/student_menu_builder';
import { TStudentMenuDatasource } from '../datasource/student_menu_datasource';
import { Hold, HoldPriorityFeedQuery } from 'capability-ng-holds';
import { Survey } from 'capability-ng-survey/src/builders/survey_builder';
import { IStudygroupsPriorityFeedDatasource } from 'capability-ng-study-groups/src/datasources/study_groups_priority_feed_datasource';
import { IApptSummariesDatasource } from 'capability-ng-reports/src/datasources/appt_summaries_datasource';
import { INotesDatasource } from 'capability-ng-reports/src/datasources/notes_datasource';
import { IProgressReportsDatasource } from 'capability-ng-reports/src/datasources/progress_reports_datasource';
import { STCConfigsModel, KnowledgeBotQuery, KnowledgeBotModel } from 'capability-ng-priority-feed';

export class StudentMenuQuery implements IQuery {
  private _result: {
    menuItems: StudentMenu[];
  };

  constructor(
    private ngStudentMenuDatasource: TStudentMenuDatasource,
    private ngToDoPriorityFeedQuery: ToDoPriorityFeedQuery,
    private staffTodoListDatastore: StaffTodoListDatastore,
    private reminderListDatastore: ReminderListDatastore,
    private ngPathItemDatastore: IDatasource<PathItem>,
    private ngPathTaskDatastore: IDatasource<PathItem>,
    private ngConversationDatastore: IDatasource<Conversation, IConversationDatasourceModel>,
    private ngConversationsPriorityFeedQuery: ConversationsPriorityFeedQuery,
    private ngAppointmentRequestPriorityFeedQuery: AppointmentRequestPriorityFeedQuery,
    private ngHoldPriorityFeedQuery: HoldPriorityFeedQuery,
    private ngHoldDatastore: IDatasource<Hold>,
    private ngSurveyPriorityFeedQuery: SurveyPriorityFeedQuery,
    private ngSurveyDatastore: IDatasource<Survey>,
    private ngStudyGroupsPriorityFeedQuery: StudyGroupsPriorityFeedQuery,
    private ngStudygroupsPriorityFeedDatasource: IStudygroupsPriorityFeedDatasource,
    private ngReportsPriorityFeedQuery: ReportsPriorityFeedQuery,
    private ngApptSummariesDatasource: IApptSummariesDatasource,
    private ngNotesDatasource: INotesDatasource,
    private ngProgressReportsDatasource: IProgressReportsDatasource,
    private ngSTCConfigsModel: STCConfigsModel,
    private ngKnowledgeBotModel: KnowledgeBotModel,
    private ngKnowledgeBotQuery: KnowledgeBotQuery
  ) {
    Query.registerQuery(
      this,
      ngStudentMenuDatasource,
      staffTodoListDatastore,
      reminderListDatastore,
      ngPathItemDatastore,
      ngPathTaskDatastore,
      ngConversationDatastore,
      ngHoldDatastore,
      ngSurveyDatastore,
      ngStudygroupsPriorityFeedDatasource,
      ngApptSummariesDatasource,
      ngNotesDatasource,
      ngProgressReportsDatasource,
      ngSTCConfigsModel,
      ngKnowledgeBotModel
    );
  }

  private readonly defaultMenuList = {
    HOME: {
      uri: '#/my/priority-feed/',
      badgeCountStrategy: (menuItem: StudentMenu) => menuItem.badge_count
    },
    TODOS: {
      uri: '#/my/todos/all/',
      badgeCountStrategy: (menuItem: StudentMenu) => this.ngToDoPriorityFeedQuery.count
    },
    APPOINTMENTS: {
      uri: '#/my/host/as3/appointment-dashboard?tab_name=appointments',
      badgeCountStrategy: (menuItem: StudentMenu) => this.ngAppointmentRequestPriorityFeedQuery.count
    },
    CALENDAR: {
      uri: '#/my/calendar',
      badgeCountStrategy: (menuItem: StudentMenu) => menuItem.badge_count
    },
    DOCS: {
      uri: '#/my/reports',
      badgeCountStrategy: (menuItem: StudentMenu) => this.ngReportsPriorityFeedQuery.count
    },
    DOCS_APPOINTMENT_SUMMARY: {
      uri: '#/my/appointment-summary',
      badgeCountStrategy: (menuItem: StudentMenu) => menuItem.badge_count
    },
    DOCS_NOTES: {
      uri: '#/my/notes',
      badgeCountStrategy: (menuItem: StudentMenu) => menuItem.badge_count
    },
    DOCS_PROGRESS: {
      uri: '#/my/progress-report',
      badgeCountStrategy: (menuItem: StudentMenu) => menuItem.badge_count
    },
    HAND_RAISE: {
      uri: '#/my/self-alerts',
      badgeCountStrategy: (menuItem: StudentMenu) => menuItem.badge_count
    },
    STUDY_BUDDIES: {
      uri: '#/my/study-groups',
      badgeCountStrategy: (menuItem: StudentMenu) => this.ngStudyGroupsPriorityFeedQuery.count
    },
    RESOURCES: {
      // FIXME: the resources micro is hard-coded to link to #/my/resources
      //
      //
      uri: '#/my/host/resources/index',
      badgeCountStrategy: (menuItem: StudentMenu) => menuItem.badge_count
    },
    PLACES: {
      uri: '#/my/resources/places',
      badgeCountStrategy: (menuItem: StudentMenu) => menuItem.badge_count
    },
    PEOPLE: {
      uri: '#/my/resources/people',
      badgeCountStrategy: (menuItem: StudentMenu) => menuItem.badge_count
    },
    SURVEYS: {
      uri: '#/my/survey-home/list',
      badgeCountStrategy: (menuItem: StudentMenu) => this.ngSurveyPriorityFeedQuery.count
    },
    NOTIFICATIONS: {
      uri: '#/my/conversations/all/',
      badgeCountStrategy: (menuItem: StudentMenu) => this.ngConversationsPriorityFeedQuery.count
    },
    HOLDS: {
      uri: '#/my/hold-center/',
      badgeCountStrategy: (menuItem: StudentMenu) => this.ngHoldPriorityFeedQuery.count
    },
    CLASS_SCHEDULE: {
      uri: '#/my/course_schedule/term/',
      badgeCountStrategy: (menuItem: StudentMenu) => menuItem.badge_count
    },
    MAJOR_EXPLORER: {
      uri: '#/my/begin/',
      badgeCountStrategy: (menuItem: StudentMenu) => menuItem.badge_count
    },
    FINANCIAL_PLANNER: {
      uri: '#/my/fp/',
      badgeCountStrategy: (menuItem: StudentMenu) => menuItem.badge_count
    },
    FAVORITES: {
      uri: '#/my/favorites/',
      badgeCountStrategy: (menuItem: StudentMenu) => menuItem.badge_count
    },
    HELP: {
      uri: '#',
      badgeCountStrategy: (menuItem: StudentMenu) => menuItem.badge_count
    },
    ACCOUNT: {
      uri: '#/my/settings/',
      badgeCountStrategy: (menuItem: StudentMenu) => menuItem.badge_count
    },
    KNOWLEDGE_BOT: {
      uri: '#/my/host/kbi/',
      badgeCountStrategy: (menuItem: StudentMenu) => menuItem.badge_count
    },
    CHECKLISTS: {
      uri: '#/my/host/events/checklist/items',
      badgeCountStrategy: (menuItem: StudentMenu) => menuItem.badge_count
    },
    JOURNEYS: {
      uri: '#/my/host/events/journeys',
      badgeCountStrategy: (menuItem: StudentMenu) => menuItem.badge_count
    }
  };

  run(): StudentMenuQuery {
    this.ngToDoPriorityFeedQuery.run();
    this.ngAppointmentRequestPriorityFeedQuery.run();
    this.ngConversationsPriorityFeedQuery.run();
    this.ngHoldPriorityFeedQuery.run();
    this.ngSurveyPriorityFeedQuery.run();
    this.ngStudyGroupsPriorityFeedQuery.run();
    this.ngReportsPriorityFeedQuery.run();
    this.ngKnowledgeBotQuery.run();

    this._result = { menuItems: [] };
    this._result.menuItems = _4.filter(this.ngStudentMenuDatasource.values(), (menuItem) => menuItem.display_in_menu);
    this._result.menuItems = this._result.menuItems.map((menuItem) => {
      // read in STC config name for Hand Raise / Self Alert
      //
      if (menuItem.nk === 'HAND_RAISE') {
        menuItem.title = this.ngSTCConfigsModel.read().self_alert_label;
      }

      //Knowledge bot name is configured in navai
      if (menuItem.nk === 'KNOWLEDGE_BOT') {
        menuItem.title = this.ngKnowledgeBotQuery.result.name;
      }

      menuItem.badge_count = this.defaultMenuList[menuItem.nk]
        ? this.defaultMenuList[menuItem.nk].badgeCountStrategy(menuItem)
        : undefined;

      // we do not receive uri's for EAB controlled features, so we use the above strategy
      //
      if (!_4.isEmpty(menuItem.uri)) {
        return menuItem;
      }

      return {
        ...menuItem,
        uri: _4.get(this.defaultMenuList, menuItem.nk + '.uri', menuItem.url)
      };
    });

    this._result.menuItems = _4.orderBy(this._result.menuItems, 'rank', 'asc');
    return this;
  }

  get result() {
    return this._result;
  }
}



// WEBPACK FOOTER //
// ./app/lib/responsive_navigation/queries/student_menu_query.ts